import * as React from 'react';
import { useRef } from 'react';
import { IStringLocalizer } from '../../../utilities/IStringLocalizer';
import { SUPPORTED_FILE_TYPES } from '../../../utilities/fileUtils';
import { useLocalizer } from './hooks/useLocalizer';
import { useDragAndDrop } from './hooks/useDragAndDrop';
import { useFileValidation } from './hooks/useFileValidation';
import { AIHeader } from './AIHeader';
import AISupportedFileTypes from './AISupportedFileTypes';
import AINextSteps from './AINextSteps';

interface AIDropzoneProps {
    onFilesSelected: (files: File[]) => void;
    onError?: (errorMessage: string) => void;
    localizer?: IStringLocalizer;
}

export const AIDropzone: React.FC<AIDropzoneProps> = ({
    onFilesSelected,
    onError,
    localizer
}) => {
    const localize = useLocalizer(localizer);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Validation hook
    const { handleFiles, handleFileSelect } = useFileValidation({
        onFilesSelected,
        onError,
        errorMessage: localize("Some files were rejected. Please ensure all files are under 20MB and of supported types (.pdf, .docx, .xlsx, .csv, .txt, .ppt, .pptx, .png, .doc, .xml, .jpg/.jpeg, IPC1752A).")
    });

    // Drag and drop hook
    const { isDragActive, dragHandlers } = useDragAndDrop({
        onDrop: handleFiles
    });

    const handleBrowseClick = () => {
        fileInputRef.current?.click();
    };

    return (
        <div
            className={`ai-dropzone-content ${isDragActive ? 'drag-active' : ''}`}
            {...dragHandlers}
        >
            {/* Header Section */}
            <div className="ai-header-section">
                <AIHeader localizer={localizer} />
                <p className="ai-tagline">
                    {localize("A faster way to complete your survey")}
                </p>
                <p className="ai-description">
                    {localize("Import company compliance letters, previous survey responses from other platforms, SDS, and more. Our agent analyzes them and drafts answers to this survey for you to review.")}
                </p>

                <button type="button" className="ai-browse-button" onClick={handleBrowseClick}>
                    <i className="glyphicon glyphicon-upload" style={{ marginRight: '8px' }}></i>
                    {localize("Select Files")}
                </button>
            </div>

            <input
                ref={fileInputRef}
                id="ai-file-input"
                type="file"
                multiple
                accept={SUPPORTED_FILE_TYPES.join(', ')}
                onChange={handleFileSelect}
                style={{ display: 'none' }}
            />

            {/* Supported File Types */}
            <AISupportedFileTypes localizer={localizer} />

            {/* Next Steps Info */}
            <AINextSteps localizer={localizer} />
        </div>
    );
};

export default AIDropzone;